import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { setDeleteMaterialImportId, setUpdateMaterialImportId, downloadMaterialImportDocument } from "../../actions/materialImports";
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { Button } from 'react-bootstrap';
import { usePermission } from '../../hooks/usePermission';
import { useManagerPermissions } from '../../hooks/useManagerPermissions';
import { Download } from 'react-feather';
import ButtonLoader from "../layout/ButtonLoader";
import ColorPicker from "../layout/ColorPicker";

const formatMoney = (value, seperator = ',') => {
    if (value || value === 0) {
        // Format with exactly 2 decimal places
        const formattedValue = Number(value).toFixed(2);
        return formattedValue.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, seperator);
    }
    return '0.00';
}

const MaterialImportItem = ({
    setDeleteMaterialImportId,
    setUpdateMaterialImportId,
    downloadMaterialImportDocument,
    number,
    materialImport: {
        id,
        supplier,
        warehouse,
        warehouse_id,
        items,
        reference_number,
        total_amount,
        date,
        created_at,
        is_for_special_sale,
        importer,
        deleted_at,
        can_edit
    }
}) => {
    const { userCan } = usePermission();
    const { canManagerEditMaterialImport, canManagerDeleteMaterialImport } = useManagerPermissions();
    const [isLoading, setIsLoading] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [textColor, setTextColor] = useState('#000000');

    // Reconstruct material import object for permission checks
    const materialImportObject = {
        id, supplier, warehouse, warehouse_id, items, reference_number,
        total_amount, date, created_at, is_for_special_sale, importer,
        deleted_at, can_edit
    };

    if (!userCan('view material imports')) {
        return null;
    }

    const setDeleteMaterialImport = (id) => {
        setDeleteMaterialImportId(id);
    };

    const setUpdateMaterialImport = (id) => {
        setUpdateMaterialImportId(id);
    };

    const onDownload = async (id) => {
        setIsLoading(true);
        const data = await downloadMaterialImportDocument(id, textColor);
        if (data?.url) {
            window.open(data.url, '_blank');
        }
        setIsLoading(false);
    };

    const toggleDetails = () => {
        setShowDetails(!showDetails);
    };

    // Summarize the materials for collapsed view
    const itemsCount = items?.length || 0;
    const firstThreeItems = items?.slice(0, 3) || [];
    let materialsSummary = firstThreeItems.map(item => item.material?.name).join(', ');
    if (itemsCount > 3) {
        materialsSummary += ` va yana ${itemsCount - 3} ta...`;
    }

    // Determine if the item is deleted
    const isDeleted = !!deleted_at;

    return (
        <>
            <tr className={`${showDetails ? 'expanded-row' : ''} ${isDeleted ? 'table-danger deleted-item' : ''}`}>
                <th scope="row">{number}</th>
                <td>{reference_number || `-`}</td>
                <td>{supplier?.name}</td>
                <td>{warehouse?.name || "---"}</td>
                <td>
                    <div className="d-flex align-items-center">
                        <button 
                            className="btn btn-sm btn-outline-secondary me-2" 
                            onClick={toggleDetails}
                        >
                            <FontAwesomeIcon icon={showDetails ? faChevronUp : faChevronDown} />
                        </button>
                        {materialsSummary || "---"}
                    </div>
                </td>
                <td>{formatMoney(total_amount)}</td>
                <td>{moment(date).format('DD-MM-YYYY')}</td>
                <td>{moment(created_at).format('DD-MM-YYYY HH:mm')}</td>
                <td>{importer?.name || "---"}</td>
                <td>{is_for_special_sale ? 'Ha' : 'Yo\'q'}</td>
                <td>
                    {
                    !isDeleted ? (
                        <div className="d-flex align-items-center gap-1">
                            <ColorPicker 
                                selectedColor={textColor} 
                                onColorChange={setTextColor}
                                disabled={isLoading}
                            />
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => onDownload(id)}
                                disabled={isLoading}
                            >
                                {isLoading ? <ButtonLoader /> : <Download size={14} />}
                            </Button>
                        </div>
                    ) : ('')
                }
                    {canManagerEditMaterialImport(materialImportObject) && !isDeleted && (
                        <Button
                            variant="outline-warning"
                            size="sm"
                            className="me-1"
                            onClick={() => setUpdateMaterialImport(id)}
                        >
                            <FontAwesomeIcon icon={faEdit} />
                        </Button>
                    )}
                    {canManagerDeleteMaterialImport(materialImportObject) && !isDeleted && (
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => setDeleteMaterialImport(id)}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </Button>
                    )}
                    {isDeleted && (
                        <span className="badge bg-danger ms-1">O'chirilgan</span>
                    )}
                    {!isDeleted && !can_edit && (
                        <span className="badge bg-secondary ms-1" title="2 kundan ko'p vaqt o'tganligi sababli o'zgartirib bo'lmaydi">Muddati o'tgan</span>
                    )}
                </td>
            </tr>
            {showDetails && items && items.length > 0 && (
                <tr className={`details-row ${isDeleted ? 'table-danger deleted-item' : ''}`}>
                    <td colSpan="11">
                        <div className="details-container">
                            <h6 className="mb-3">Import elementlari ({items.length})</h6>
                            {isDeleted && (
                                <div className="alert alert-danger mb-3">
                                    <small>Bu import {moment(deleted_at).format('DD-MM-YYYY HH:mm')} da o'chirilgan</small>
                                </div>
                            )}
                            <table className="table table-sm table-bordered">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Material</th>
                                        <th>Miqdori</th>
                                        <th>Narxi</th>
                                        <th>Jami summa</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item, index) => (
                                        <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td>{item.material?.name}</td>
                                            <td>{formatMoney(item.quantity)}</td>
                                            <td>{formatMoney(item.price)}</td>
                                            <td>{formatMoney(item.total_amount || item.quantity * item.price)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan="4" className="text-end"><strong>Umumiy summa:</strong></td>
                                        <td><strong>{formatMoney(total_amount)}</strong></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

MaterialImportItem.propTypes = {
    materialImport: PropTypes.shape({
        id: PropTypes.number.isRequired,
        supplier: PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired
        }).isRequired,
        warehouse: PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string
        }),
        items: PropTypes.arrayOf(
            PropTypes.shape({
                material: PropTypes.shape({
                    id: PropTypes.number.isRequired,
                    name: PropTypes.string.isRequired
                }),
                quantity: PropTypes.number.isRequired,
                price: PropTypes.number.isRequired,
                total_amount: PropTypes.number
            })
        ),
        reference_number: PropTypes.string,
        total_amount: PropTypes.number.isRequired,
        date: PropTypes.string.isRequired,
        created_at: PropTypes.string.isRequired,
        is_for_special_sale: PropTypes.bool,
        importer: PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string
        }),
        deleted_at: PropTypes.string,
        can_edit: PropTypes.bool
    }).isRequired,
    setDeleteMaterialImportId: PropTypes.func.isRequired,
    setUpdateMaterialImportId: PropTypes.func.isRequired,
    downloadMaterialImportDocument: PropTypes.func.isRequired,
    number: PropTypes.number.isRequired
};

export default connect(null, {
    setDeleteMaterialImportId,
    setUpdateMaterialImportId,
    downloadMaterialImportDocument
})(MaterialImportItem);
